import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { AnimatedCurrency } from '@/components/ui/animated-number'
import { TrendingUp, TrendingDown, Wallet, Building2, CreditCard } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import api from '@/lib/api'
import type { NetWorth, NetWorthHistory, ExpenseBreakdown } from '@/types'
import { useViewModeStore } from '@/stores/viewModeStore'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Filler,
  ArcElement,
  Legend,
} from 'chart.js'
import { Line, Doughnut, Bar } from 'react-chartjs-2'
import { format, subMonths, startOfMonth, parseISO, isWithinInterval } from 'date-fns'
import type { Transaction } from '@/types'
import { PerksDashboardCard } from '@/components/perks/PerksDashboardCard'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Filler,
  ArcElement,
  Legend
)

export function DashboardPage() {
  const navigate = useNavigate()
  const viewMode = useViewModeStore((state) => state.viewMode)
  const [netWorth, setNetWorth] = useState<NetWorth | null>(null)
  const [netWorthHistory, setNetWorthHistory] = useState<NetWorthHistory[]>([])
  const [expenseBreakdown, setExpenseBreakdown] = useState<ExpenseBreakdown[]>([])
  const [monthlyExpenses, setMonthlyExpenses] = useState<{ month: string; total: number }[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [historyDays, setHistoryDays] = useState(30)
  const [expenseRange, setExpenseRange] = useState<'MTD' | 'QTD' | 'YTD'>('MTD')

  const normalizeExpenseBreakdown = (items: unknown): ExpenseBreakdown[] => {
    if (!Array.isArray(items)) {
      return []
    }

    return items.map((item) => {
      const entry =
        typeof item === 'object' && item !== null
          ? (item as Record<string, unknown>)
          : {}
      const userCategory =
        typeof entry.user_category === 'object' && entry.user_category !== null
          ? (entry.user_category as Record<string, unknown>)
          : null

      const categoryName =
        (typeof userCategory?.name === 'string' && userCategory.name) ||
        (typeof entry.category === 'string' && entry.category) ||
        (typeof entry.category_name === 'string' && entry.category_name) ||
        'Other'

      const icon =
        (typeof userCategory?.icon === 'string' && userCategory.icon) ||
        (typeof entry.icon === 'string' && entry.icon) ||
        ''

      const color =
        (typeof userCategory?.color === 'string' && userCategory.color) ||
        (typeof entry.color === 'string' && entry.color) ||
        '#e5e7eb'

      const excludeFromSpend =
        (typeof userCategory?.exclude_from_spend === 'boolean' &&
          userCategory.exclude_from_spend) ||
        (typeof entry.exclude_from_spend === 'boolean' &&
          entry.exclude_from_spend) ||
        false

      const idRaw = userCategory?.id ?? entry.category_id
      const id = typeof idRaw === 'number' && Number.isFinite(idRaw) ? idRaw : 0

      const totalRaw = entry.total ?? entry.total_amount
      const total = Number.isFinite(Number(totalRaw)) ? Number(totalRaw) : 0

      const percentageRaw = entry.percentage
      const percentage = Number.isFinite(Number(percentageRaw))
        ? Number(percentageRaw)
        : 0

      const countRaw = entry.count ?? entry.transaction_count ?? entry.transactionCount
      const count = Number.isFinite(Number(countRaw)) ? Number(countRaw) : 0

      return {
        category: categoryName,
        user_category: {
          id,
          name: categoryName,
          color,
          icon,
          exclude_from_spend: excludeFromSpend,
          is_active: true,
        },
        total,
        percentage,
        count,
      }
    })
  }

  const buildExpenseBreakdownUrl = (range: 'MTD' | 'QTD' | 'YTD') => {
    let query = 'period=month'

    if (range === 'QTD') {
      query = 'period=qtd'
    } else if (range === 'YTD') {
      query = 'period=ytd'
    }

    return `/expense_breakdown?${query}`
  }

  // Compute monthly expenses from transactions
  const computeMonthlyExpenses = useCallback((transactions: Transaction[]) => {
    const now = new Date()
    const months: { month: string; total: number; startDate: Date; endDate: Date }[] = []

    // Build 12 months of data
    for (let i = 11; i >= 0; i--) {
      const monthStart = startOfMonth(subMonths(now, i))
      const monthEnd = i === 0 ? now : startOfMonth(subMonths(now, i - 1))
      months.push({
        month: format(monthStart, 'MMM'),
        total: 0,
        startDate: monthStart,
        endDate: monthEnd,
      })
    }

    // Sum expenses by month (exclude categories marked exclude_from_spend)
    transactions.forEach((txn) => {
      if (!txn.date || txn.amount >= 0) return // Skip income
      if (txn.user_category?.exclude_from_spend) return

      const txnDate = parseISO(txn.date)
      const monthData = months.find((m) =>
        isWithinInterval(txnDate, { start: m.startDate, end: m.endDate })
      )
      if (monthData) {
        monthData.total += Math.abs(txn.amount)
      }
    })

    return months.map((m) => ({ month: m.month, total: m.total }))
  }, [])

  useEffect(() => {
    async function fetchData() {
      try {
        const expenseUrl = buildExpenseBreakdownUrl(expenseRange)
        // Fetch transactions for the last 12 months for month-over-month chart
        const startDate = format(subMonths(new Date(), 12), 'yyyy-MM-dd')
        const endDate = format(new Date(), 'yyyy-MM-dd')

        const [nwResponse, historyResponse, expenseResponse, txResponse] = await Promise.all([
          api.get('/net_worth', { params: { view: viewMode } }),
          api.get('/net_worth_history', { params: { days: historyDays, view: viewMode } }),
          api.get(expenseUrl, { params: { view: viewMode } }),
          api.get('/transactions', { params: { start_date: startDate, end_date: endDate, view: viewMode } }),
        ])
        setNetWorth(nwResponse.data)
        setNetWorthHistory(historyResponse.data.history)
        setExpenseBreakdown(normalizeExpenseBreakdown(expenseResponse.data?.breakdown))
        setMonthlyExpenses(computeMonthlyExpenses(txResponse.data.transactions || []))
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [historyDays, expenseRange, viewMode, computeMonthlyExpenses])

  const chartData = {
    labels: netWorthHistory.map((d) =>
      new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    ),
    datasets: [
      {
        label: 'Net Worth',
        data: netWorthHistory.map((d) => d.net_worth),
        borderColor: '#38bdf8',
        backgroundColor: 'rgba(56, 189, 248, 0.12)',
        fill: true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 6,
      },
    ],
  }

  const formatAxisCurrency = (value: number | string) => {
    const numeric = Number(value)
    if (!Number.isFinite(numeric)) {
      return ''
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'standard',
      maximumFractionDigits: 0,
    }).format(numeric)
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#18181b',
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 12,
        displayColors: false,
        callbacks: {
          label: (context: { parsed: { y: number | null } }) =>
            formatCurrency(context.parsed.y ?? 0),
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#71717a', maxTicksLimit: 7 },
      },
      y: {
        grid: { display: false },
        ticks: {
          color: '#71717a',
          callback: (value: number | string) => formatAxisCurrency(value),
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  }

  // Filter out categories marked as "exclude from spend" and sort by total amount (largest first)
  const sortedExpenseBreakdown = [...expenseBreakdown]
    .filter((e) => !e.user_category?.exclude_from_spend)
    .sort((a, b) => Math.abs(b.total) - Math.abs(a.total))

  const expenseChartData = {
    labels: sortedExpenseBreakdown.slice(0, 6).map((e) => e.user_category?.name || e.category || 'Other'),
    datasets: [
      {
        data: sortedExpenseBreakdown.slice(0, 6).map((e) => Math.abs(e.total)),
        backgroundColor: [
          '#667eea',
          '#10b981',
          '#f59e0b',
          '#ef4444',
          '#8b5cf6',
          '#06b6d4',
        ],
        borderWidth: 0,
      },
    ],
  }

  const expenseChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    cutout: '70%',
  }

  const handleExpenseClick = useCallback(
    (categoryId?: number | null) => {
      if (!categoryId) {
        return
      }
      navigate(`/transactions?category_id=${encodeURIComponent(categoryId)}`)
    },
    [navigate]
  )

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-5 w-40" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  const netWorthChange = netWorthHistory.length > 1
    ? netWorthHistory[netWorthHistory.length - 1].net_worth - netWorthHistory[0].net_worth
    : 0
  const isPositiveChange = netWorthChange >= 0

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Net Worth
            </CardTitle>
            <Wallet className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              <AnimatedCurrency value={netWorth?.net_worth || 0} />
            </div>
            <div
              className={`mt-1 flex items-center text-sm ${
                isPositiveChange ? 'text-success' : 'text-danger'
              }`}
            >
              {isPositiveChange ? (
                <TrendingUp className="mr-1 h-4 w-4" />
              ) : (
                <TrendingDown className="mr-1 h-4 w-4" />
              )}
              <AnimatedCurrency value={Math.abs(netWorthChange)} /> ({historyDays}d)
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Total Assets
            </CardTitle>
            <Building2 className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">
              <AnimatedCurrency value={netWorth?.total_assets || 0} />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Cash, investments, property
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">
              Total Liabilities
            </CardTitle>
            <CreditCard className="h-4 w-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-danger">
              <AnimatedCurrency value={netWorth?.total_liabilities || 0} />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Loans, credit cards, debts
            </p>
          </CardContent>
        </Card>
      </div>

      <PerksDashboardCard />

      {/* Net Worth Chart */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Net Worth Trend</CardTitle>
          <div className="flex gap-1">
            {[7, 30, 90].map((days) => (
              <Button
                key={days}
                variant={historyDays === days ? 'default' : 'outline'}
                size="sm"
                onClick={() => setHistoryDays(days)}
              >
                {days}d
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <Line data={chartData} options={chartOptions} />
          </div>
        </CardContent>
      </Card>

      {/* Expense Breakdown */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Expense Breakdown</CardTitle>
          <div className="flex gap-1">
            {(['MTD', 'QTD', 'YTD'] as const).map((range) => (
              <Button
                key={range}
                variant={expenseRange === range ? 'default' : 'outline'}
                size="sm"
                onClick={() => setExpenseRange(range)}
              >
                {range}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="h-64">
              <Doughnut data={expenseChartData} options={expenseChartOptions} />
            </div>
            <div className="space-y-3">
              {sortedExpenseBreakdown.slice(0, 6).map((expense, index) => {
                const colors = [
                  '#667eea',
                  '#10b981',
                  '#f59e0b',
                  '#ef4444',
                  '#8b5cf6',
                  '#06b6d4',
                ]
                const expenseKey = expense.user_category?.id
                  ? `user-${expense.user_category.id}`
                  : expense.category
                    ? `category-${expense.category}-${index}`
                    : `index-${index}`
                const categoryLabel =
                  expense.user_category?.name || expense.category || 'Other'
                const isClickable = Boolean(expense.user_category?.id)
                return (
                  <button
                    key={expenseKey}
                    type="button"
                    className={`flex w-full items-center justify-between rounded-lg px-2 py-1 text-left transition ${
                      isClickable
                        ? 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                        : 'cursor-default'
                    }`}
                    onClick={() => handleExpenseClick(expense.user_category?.id)}
                    disabled={!isClickable}
                    aria-label={
                      isClickable ? `View ${categoryLabel} transactions` : undefined
                    }
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: colors[index] }}
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {expense.user_category?.icon}{' '}
                        {categoryLabel}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {formatCurrency(Math.abs(expense.total))}
                      </div>
                      <div className="text-xs text-gray-500">
                        {expense.percentage.toFixed(1)}%
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Month Over Month Expenses */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Month Over Month</CardTitle>
          {monthlyExpenses.length > 0 && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Last month: {formatCurrency(monthlyExpenses[monthlyExpenses.length - 2]?.total || 0)}
            </span>
          )}
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <Bar
              data={{
                labels: monthlyExpenses.map((m) => m.month),
                datasets: [
                  {
                    data: monthlyExpenses.map((m) => m.total),
                    backgroundColor: 'rgba(249, 115, 22, 0.8)',
                    borderRadius: 6,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (context) => formatCurrency(context.raw as number),
                    },
                  },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    ticks: { color: '#9ca3af' },
                  },
                  y: {
                    grid: { color: 'rgba(156, 163, 175, 0.1)' },
                    ticks: {
                      color: '#9ca3af',
                      callback: (value) => {
                        const num = Number(value)
                        if (num >= 1000) return `$${(num / 1000).toFixed(0)}K`
                        return `$${num}`
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
